import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, BookOpen, Video, FileText, Mail, Ticket, Edit2, Trash2, MessageSquare, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SEOHead } from "@/components/SEOHead";

export default function Support() {
  const { toast } = useToast();
  const { user, subscription } = useAuth();
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [viewTicketDialogOpen, setViewTicketDialogOpen] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");

  const isLoggedIn = !!user;
  const hasActiveSubscription = subscription?.status === 'active';
  const canSubmitTickets = isLoggedIn && hasActiveSubscription;

  useEffect(() => {
    if (canSubmitTickets) {
      fetchTickets();
    }
  }, [canSubmitTickets]);

  const fetchTickets = async () => {
    const { data } = await supabase
      .from("support_tickets")
      .select("*")
      .eq("user_id", user?.id)
      .order("created_at", { ascending: false });
    
    setTickets(data || []);
  };

  const fetchComments = async (ticketId: string) => {
    const { data } = await supabase
      .from("ticket_comments")
      .select(`
        *,
        profiles:user_id (full_name, email)
      `)
      .eq("ticket_id", ticketId)
      .order("created_at", { ascending: true });
    
    setComments(data || []);
  };

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !canSubmitTickets) return;

    setIsSubmitting(true);

    const { error } = await supabase
      .from("support_tickets")
      .insert({
        user_id: user.id,
        subject,
        category,
        description,
        status: 'open',
        priority: 'medium'
      });

    setIsSubmitting(false);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Ticket submitted!",
        description: "Our support team will respond soon.",
      });
      setSubject("");
      setCategory("");
      setDescription("");
      setTicketDialogOpen(false);
      fetchTickets();
    }
  };

  const handleUpdateTicket = async (ticketId: string, updates: any) => {
    const { error } = await supabase
      .from("support_tickets")
      .update(updates)
      .eq("id", ticketId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Ticket updated",
        description: "Your ticket has been updated successfully.",
      });
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, ...updates });
      }
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    const { error } = await supabase
      .from("support_tickets")
      .delete()
      .eq("id", ticketId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Ticket deleted",
        description: "Your ticket has been deleted successfully.",
      });
      fetchTickets();
      setViewTicketDialogOpen(false);
    }
  };

  const handleViewTicket = async (ticket: any) => {
    setSelectedTicket(ticket);
    await fetchComments(ticket.id);
    setViewTicketDialogOpen(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;

    const { error } = await supabase
      .from("ticket_comments")
      .insert({
        ticket_id: selectedTicket.id,
        user_id: user?.id,
        comment: newComment,
        is_admin_reply: false
      });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setNewComment("");
      await fetchComments(selectedTicket.id);
    }
  };

  const articles = [
    {
      title: "Getting Started with Mollie Payments",
      category: "Payments",
      icon: CreditCard,
      description: "Complete guide to setting up Mollie payment processing",
      slug: "getting-started-with-mollie"
    },
    {
      title: "Getting Started with PTA Hub",
      category: "Getting Started",
      icon: BookOpen,
      description: "Learn the basics of setting up your PTA account",
      slug: "getting-started-with-pta-hub"
    },
    {
      title: "Managing Member Roles",
      category: "Administration",
      icon: FileText,
      description: "How to assign and manage member permissions",
      slug: "managing-member-roles"
    },
    {
      title: "Creating Your First Event",
      category: "Events",
      icon: Video,
      description: "Step-by-step guide to event planning",
      slug: "creating-your-first-event"
    },
    {
      title: "Setting Up Fundraising Goals",
      category: "Fundraising",
      icon: FileText,
      description: "Track and manage your fundraising objectives",
      slug: "setting-up-fundraising-goals"
    },
    {
      title: "Using the Raffle Game",
      category: "Games",
      icon: Video,
      description: "Complete guide to running digital raffles",
      slug: "using-the-raffle-game"
    },
    {
      title: "Financial Reporting Guide",
      category: "Finance",
      icon: FileText,
      description: "Generate and export financial reports",
      slug: "financial-reporting-guide"
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <SEOHead slug="support" />
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">Support Centre</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Find answers to your questions or get in touch with our team
        </p>
      </div>

      {/* Search */}
      <Card className="shadow-card max-w-3xl mx-auto">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search for help articles, guides, and videos..."
              className="pl-10 h-12 text-lg"
            />
          </div>
        </CardContent>
      </Card>

      {/* Support Tickets Section - Only for logged in members with active subscription */}
      {canSubmitTickets && (
        <Card className="shadow-card max-w-6xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-primary" />
                  My Support Tickets
                </CardTitle>
                <CardDescription>View and manage your support tickets</CardDescription>
              </div>
              <Button onClick={() => setTicketDialogOpen(true)}>
                <Ticket className="h-4 w-4 mr-2" />
                Submit Ticket
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {tickets.length > 0 ? (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => handleViewTicket(ticket)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{ticket.subject}</h4>
                        <Badge variant="outline" className="text-xs">
                          {ticket.category}
                        </Badge>
                        <Badge className={`${getStatusColor(ticket.status)} text-xs`}>
                          {ticket.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Created {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTicket(ticket.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No tickets yet. Click "Submit Ticket" to create your first support ticket.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Articles */}
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">Popular Articles</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((article, i) => {
            const Icon = article.icon;
            const cardContent = (
              <Card className="shadow-card hover:shadow-elevated transition-shadow cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{article.title}</CardTitle>
                      <CardDescription className="text-xs mt-1">{article.category}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{article.description}</p>
                  <Button variant="link" className="px-0 mt-2">Read more →</Button>
                </CardContent>
              </Card>
            );
            
            return article.slug ? (
              <Link key={i} to={`/support/${article.slug}`} className="block">
                {cardContent}
              </Link>
            ) : (
              <div key={i}>{cardContent}</div>
            );
          })}
        </div>
      </div>

      {/* Video Tutorials */}
      <Card className="shadow-card max-w-6xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            Video Tutorials
          </CardTitle>
          <CardDescription>Watch step-by-step guides</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="aspect-video bg-muted rounded mb-3 flex items-center justify-center">
                <Video className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-1">Quick Start Guide</h4>
              <p className="text-sm text-muted-foreground">5 min tutorial</p>
            </div>
            <div className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="aspect-video bg-muted rounded mb-3 flex items-center justify-center">
                <Video className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-1">Event Management</h4>
              <p className="text-sm text-muted-foreground">8 min tutorial</p>
            </div>
            <div className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="aspect-video bg-muted rounded mb-3 flex items-center justify-center">
                <Video className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="font-semibold mb-1">Financial Reports</h4>
              <p className="text-sm text-muted-foreground">6 min tutorial</p>
            </div>
            <a 
              href="https://youtu.be/Bgxkhmt4vLs" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer block"
            >
              <div className="aspect-video bg-muted rounded mb-3 overflow-hidden">
                <img 
                  src={new URL("@/assets/shop-video-thumbnail.jpg", import.meta.url).href}
                  alt="PTA Shop Tutorial"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold mb-1">PTA Shop</h4>
              <p className="text-sm text-muted-foreground">Tutorial video</p>
            </a>
            <a 
              href="https://youtu.be/3FGUHDQpP2k" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-4 border border-border rounded-lg hover:bg-accent transition-colors cursor-pointer block"
            >
              <div className="aspect-video bg-muted rounded mb-3 overflow-hidden">
                <img 
                  src={new URL("@/assets/library-video-thumbnail.jpg", import.meta.url).href}
                  alt="Library Tutorial"
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold mb-1">Library</h4>
              <p className="text-sm text-muted-foreground">Tutorial video</p>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Submit Ticket Dialog */}
      <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Submit Support Ticket</DialogTitle>
            <DialogDescription>
              Provide details about your issue and our team will respond soon
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitTicket} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ticket-subject">Subject *</Label>
              <Input
                id="ticket-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticket-category">Category *</Label>
              <Select value={category} onValueChange={setCategory} required>
                <SelectTrigger id="ticket-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">Technical Support</SelectItem>
                  <SelectItem value="billing">Billing & Payments</SelectItem>
                  <SelectItem value="features">Feature Request</SelectItem>
                  <SelectItem value="account">Account Management</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ticket-description">Description *</Label>
              <Textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide as much detail as possible..."
                rows={6}
                required
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setTicketDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Ticket"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Edit Ticket Dialog */}
      <Dialog open={viewTicketDialogOpen} onOpenChange={setViewTicketDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTicket?.subject}
              <Badge className={getStatusColor(selectedTicket?.status)}>
                {selectedTicket?.status}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Ticket #{selectedTicket?.id?.slice(0, 8)} • Created {new Date(selectedTicket?.created_at).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Textarea
                value={selectedTicket?.description || ""}
                onChange={(e) =>
                  setSelectedTicket({ ...selectedTicket, description: e.target.value })
                }
                rows={4}
                className="mt-2"
              />
              <Button
                size="sm"
                className="mt-2"
                onClick={() =>
                  handleUpdateTicket(selectedTicket.id, {
                    description: selectedTicket.description,
                  })
                }
              >
                Update Description
              </Button>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold flex items-center gap-2 mb-3">
                <MessageSquare className="h-4 w-4" />
                Comments & Replies
              </h3>
              <div className="space-y-3 mb-4">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`p-3 rounded-lg ${
                      comment.is_admin_reply ? "bg-primary/10 border-l-4 border-primary" : "bg-muted"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {comment.is_admin_reply ? "Support Team" : "You"}
                        </span>
                        {comment.is_admin_reply && (
                          <Badge variant="secondary" className="text-xs">Admin</Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm">{comment.comment}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-comment">Add Comment</Label>
                <Textarea
                  id="new-comment"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Type your message..."
                  rows={3}
                />
                <Button onClick={handleAddComment} size="sm">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Add Comment
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}